import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Badge, ProgressBar, Row, Col, Alert } from 'react-bootstrap'; // Added Row, Col, Alert
import MacronutrientChart from '../ui/MacronutrientChart'; // Import the new child component

// Bar component (remains the same as your last version)
const Bar = ({ label, value = 0 }) => {
    const [animatedValue, setAnimatedValue] = useState(0);
    const getStatus = (val) => {
        const numericVal = Number(val);
        if (numericVal > 70) return { variant: 'success', label: 'High' };
        if (numericVal > 30) return { variant: 'warning', label: 'Medium' };
        return { variant: 'danger', label: 'Low' };
    };
    const numericValue = Number(value);
    const { variant, label: statusLabel } = getStatus(numericValue);
    useEffect(() => {
        setAnimatedValue(0); let currentVal = 0;
        const targetVal = isNaN(numericValue) ? 0 : numericValue;
        const increment = targetVal === 0 ? 0 : targetVal / (800 / 10);
        if (targetVal === 0) { setAnimatedValue(0); return; }
        const interval = setInterval(() => {
            currentVal += increment;
            if (currentVal < targetVal) { setAnimatedValue(currentVal); }
            else { setAnimatedValue(targetVal); clearInterval(interval); }
        }, 10);
        return () => clearInterval(interval);
    }, [numericValue]);
    return (
        <div className="clean-bar-container mb-3">
            <div className="d-flex justify-content-between align-items-center mb-1">
                <span className="fw-medium small text-secondary">{label}</span>
                <div><span className="fw-bold me-2 small">{typeof numericValue === 'number' && !isNaN(numericValue) ? numericValue.toFixed(1) : '0'}%</span><Badge pill bg={variant} className="small py-1 px-2">{statusLabel}</Badge></div>
            </div>
            <ProgressBar now={typeof animatedValue === 'number' && !isNaN(animatedValue) ? animatedValue : 0} variant={variant} style={{ height: '8px', borderRadius: '4px' }} />
        </div>
    );
};

// InfoCard component (remains the same as your last version)
const InfoCard = ({ title, value, icon, unit = '', colorClass = 'info-card-neutral' }) => {
    const displayValue = (val) => {
        if (typeof val === 'number' && !isNaN(val)) { return val; }
        if (typeof val === 'string') { return val; }
        return 'N/A';
    };
    return (
        <div className={`info-card shadow-sm rounded p-3 text-center ${colorClass}`}>
            <div className="info-icon fs-4 mb-1">{icon}</div>
            <div className="info-text"><div className="info-title small text-muted text-uppercase">{title}</div><div className="info-value fw-bold fs-5">{displayValue(value)} {unit}</div></div>
        </div>
    );
};

const NutritionBreakdown = ({ nutrition }) => {
    const calories = nutrition?.calories || 'N/A';

    const carbsGramsRaw = nutrition?.macronutrients?.carbs;
    const proteinGramsRaw = nutrition?.macronutrients?.protein;
    const fatGramsRaw = nutrition?.macronutrients?.fat;

    const carbsGrams = typeof carbsGramsRaw === 'number' ? carbsGramsRaw : (typeof carbsGramsRaw === 'string' && !isNaN(parseFloat(carbsGramsRaw)) ? parseFloat(carbsGramsRaw) : 0);
    const proteinGrams = typeof proteinGramsRaw === 'number' ? proteinGramsRaw : (typeof proteinGramsRaw === 'string' && !isNaN(parseFloat(proteinGramsRaw)) ? parseFloat(proteinGramsRaw) : 0);
    const fatGrams = typeof fatGramsRaw === 'number' ? fatGramsRaw : (typeof fatGramsRaw === 'string' && !isNaN(parseFloat(fatGramsRaw)) ? parseFloat(fatGramsRaw) : 0);

    const sugarDV = nutrition?.dailyValuePercentages?.sugar;
    const sodiumDV = nutrition?.dailyValuePercentages?.sodium;
    const fatDV = nutrition?.dailyValuePercentages?.fat;
    const fiberDV = nutrition?.dailyValuePercentages?.fiber;

    const artificialContent = nutrition?.artificialContent || 'N/A';
    const preservatives = nutrition?.preservatives || 'N/A';
    const transFat = nutrition?.transFat || 'N/A';
    const fiberAmount = nutrition?.fiber || 'N/A';

    const showMacroChart = carbsGrams > 0 || proteinGrams > 0 || fatGrams > 0;

    return (
        <Card className="clean-nutrition-card shadow-sm border-0 rounded-3 mb-4">
            <Card.Header as="h5" className="bg-light text-primary border-bottom fw-bold p-3">
                <span className="header-icon me-2">🍽️</span> Nutrition Breakdown
            </Card.Header>

            <Card.Body className="p-3 p-md-4">
                <div className="calorie-section text-center mb-4 p-3 bg-primary-soft rounded shadow-sm">
                    <div className="calorie-label text-muted small text-uppercase">CALORIES</div>
                    <div className="calorie-badge display-4 fw-bolder text-primary">{calories}</div>
                </div>

                <h3 className="section-title fs-5 fw-semibold text-secondary mb-2 mt-4 text-center">Macronutrient Caloric Ratio (per 100 g)</h3>
                <div className="divider mb-3"></div>
                {showMacroChart ? (
                    <MacronutrientChart
                        carbsGrams={carbsGrams}
                        proteinGrams={proteinGrams}
                        fatGrams={fatGrams}
                    />
                ) : (
                    <Alert variant="light" className="text-center text-muted small py-2">Macronutrient data not available to display chart.</Alert>
                )}


                <h3 className="section-title fs-5 fw-semibold text-secondary mb-2 mt-4 pt-3 border-top">Daily Value Percentages</h3>
                <div className="divider mb-3"></div>
                {typeof sugarDV === 'number' && <Bar label="Sugar" value={sugarDV} />}
                {typeof sodiumDV === 'number' && <Bar label="Sodium" value={sodiumDV} />}
                {typeof fatDV === 'number' && <Bar label="Fat" value={fatDV} />}
                {typeof fiberDV === 'number' && <Bar label="Fiber" value={fiberDV} />}

                <h3 className="section-title fs-5 fw-semibold text-secondary mb-2 mt-4 pt-3 border-top">Additional Information</h3>
                <div className="divider mb-3"></div>
                <div className="info-cards-container row g-3">
                    <div className="col-md-3 col-6"><InfoCard title="Fiber Amount" value={fiberAmount} icon="🌾" colorClass="info-card-fiber" /></div>
                    <div className="col-md-3 col-6"><InfoCard title="Trans Fat" value={transFat} icon="⚠️" colorClass={transFat === '0g' || transFat === 'N/A' ? 'info-card-good' : 'info-card-warning'} /></div>
                    <div className="col-md-3 col-6"><InfoCard title="Preservatives" value={preservatives} icon="🛡️" colorClass={preservatives === 'none (based on fresh ingredients)' || preservatives === 'N/A' ? 'info-card-good' : 'info-card-neutral'} /></div>
                    <div className="col-md-3 col-6"><InfoCard title="Artificial Content" value={artificialContent} icon="🧪" colorClass={artificialContent.toLowerCase().includes('low') || artificialContent.toLowerCase().includes('none') || artificialContent === 'N/A' ? 'info-card-good' : 'info-card-neutral'} /></div>
                </div>
            </Card.Body>
        </Card>
    );
};

NutritionBreakdown.propTypes = {
    nutrition: PropTypes.shape({
        calories: PropTypes.string,
        macronutrients: PropTypes.shape({
            carbs: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            protein: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            fat: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        }),
        sugar: PropTypes.string,
        sodium: PropTypes.string,
        fiber: PropTypes.string,
        artificialContent: PropTypes.string,
        preservatives: PropTypes.string,
        transFat: PropTypes.string,
        dailyValuePercentages: PropTypes.shape({
            sugar: PropTypes.number,
            sodium: PropTypes.number,
            fat: PropTypes.number,
            protein: PropTypes.number,
            fiber: PropTypes.number
        })
    })
};

export default NutritionBreakdown;